/**
 * Environment Detection Utilities
 * 
 * Centralized, single source of truth for environment detection.
 * All environment-related logic should use these helpers.
 * 
 * @module utils/environment
 * @security SEC-ENV-001: Environment detection must be centralized
 */

// ============================================================================
// Types
// ============================================================================

export type AppEnvironment = 'production' | 'staging' | 'development' | 'test';

export interface EnvironmentConfig {
  /** Current environment */
  environment: AppEnvironment;
  /** Whether this is a production environment */
  isProduction: boolean;
  /** Whether this is a development environment */
  isDevelopment: boolean;
  /** Whether this is a test environment */
  isTest: boolean;
  /** Whether demo mode is allowed in this environment */
  isDemoAllowed: boolean;
  /** Base API URL for this environment */
  apiUrl: string;
}

// ============================================================================
// Environment Detection Constants
// ============================================================================

/**
 * Production hostnames
 * Add new production domains here when deploying to new environments
 */
const PRODUCTION_HOSTNAMES = [
  'cyberraptor.ai',
  'd4kr1pky3lrng.cloudfront.net', // Main portal CloudFront
] as const;

/**
 * Staging hostnames
 */
const STAGING_HOSTNAMES = [
  'staging.cyberraptor.ai',
  'staging-portal.cyberraptor.ai',
] as const;

// ============================================================================
// Memoization Cache
// ============================================================================

/** Cached environment result (computed once, never changes at runtime) */
let cachedEnvironment: AppEnvironment | null = null;

/** Frozen demo allowed flag (computed once, cannot be manipulated) */
let frozenDemoAllowed: boolean | null = null;

/**
 * Reset environment cache (FOR TESTING ONLY)
 * This should never be called in production code
 * @internal
 */
export function __resetEnvironmentCache(): void {
  if (import.meta.env.MODE !== 'test' && !import.meta.env.VITEST) {
    console.warn('[Environment] Attempted to reset environment cache outside of test mode');
    return;
  }
  cachedEnvironment = null;
  frozenDemoAllowed = null;
}

// ============================================================================
// Core Detection Functions
// ============================================================================

/**
 * Detect current environment
 * 
 * Priority order:
 * 1. VITE_APP_ENV explicit environment variable (highest priority)
 * 2. VITE_PRODUCTION flag for production override
 * 3. Test environment detection
 * 4. Hostname detection for deployed environments
 * 5. Vite's import.meta.env.MODE (build-time)
 * 6. localhost/127.0.0.1 detection
 * 7. Default to development
 * 
 * Result is memoized - environment cannot change at runtime.
 * 
 * @returns Current application environment
 */
export function detectEnvironment(): AppEnvironment {
  // Return cached result if available (performance optimization)
  if (cachedEnvironment !== null) {
    return cachedEnvironment;
  }
  
  const detected = detectEnvironmentInternal();
  cachedEnvironment = detected;
  
  // Also freeze the demo allowed flag at the same time
  frozenDemoAllowed = detected === 'development' || detected === 'test';
  
  return detected;
}

/**
 * Internal environment detection logic (not memoized)
 */
function detectEnvironmentInternal(): AppEnvironment {
  // 1. Explicit environment variable (highest priority)
  const explicitEnv = import.meta.env.VITE_APP_ENV;
  if (explicitEnv) {
    const normalizedEnv = explicitEnv.toLowerCase();
    if (['production', 'staging', 'development', 'test'].includes(normalizedEnv)) {
      return normalizedEnv as AppEnvironment;
    }
  }
  
  // 2. Production flag override
  if (import.meta.env.VITE_PRODUCTION === 'true') {
    return 'production';
  }
  
  // 3. Test environment detection
  if (import.meta.env.MODE === 'test' || 
      import.meta.env.VITEST === 'true' ||
      typeof (globalThis as unknown as { describe?: unknown }).describe === 'function') {
    return 'test';
  }
  
  // 4. Hostname-based detection (only in browser)
  if (typeof window !== 'undefined' && window.location?.hostname) {
    const hostname = window.location.hostname.toLowerCase();
    
    // Production check
    if (PRODUCTION_HOSTNAMES.some(h => hostname === h || hostname.endsWith(`.${h}`))) {
      return 'production';
    }
    
    // Staging check
    if (STAGING_HOSTNAMES.some(h => hostname === h || hostname.endsWith(`.${h}`))) {
      return 'staging';
    }
    
    // CloudFront detection (production by default)
    if (hostname.includes('.cloudfront.net')) {
      return 'production';
    }
    
    // 6. Explicit localhost/127.0.0.1 detection - definitely development
    if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '0.0.0.0') {
      return 'development';
    }
  }
  
  // 5. Vite's build mode
  if (import.meta.env.PROD) {
    return 'production';
  }
  
  // 7. Default to development
  return 'development';
}

/**
 * Check if current environment is production
 * 
 * @returns true if running in production
 */
export function isProduction(): boolean {
  return detectEnvironment() === 'production';
}

/**
 * Check if current environment is development
 * 
 * @returns true if running in development
 */
export function isDevelopment(): boolean {
  return detectEnvironment() === 'development';
}

/**
 * Check if current environment is staging
 * 
 * @returns true if running in staging
 */
export function isStaging(): boolean {
  return detectEnvironment() === 'staging';
}

/**
 * Check if current environment is test
 * 
 * @returns true if running in test environment
 */
export function isTest(): boolean {
  return detectEnvironment() === 'test';
}

/**
 * Check if demo mode is allowed in current environment
 * 
 * Demo mode is ONLY allowed in:
 * - Development
 * - Test
 * 
 * Demo mode is NEVER allowed in:
 * - Production
 * - Staging
 * 
 * SECURITY: Result is frozen at initialization to prevent runtime manipulation
 * 
 * @returns true if demo mode is allowed
 */
export function isDemoAllowed(): boolean {
  // Use frozen result if available (computed during first detectEnvironment call)
  if (frozenDemoAllowed !== null) {
    return frozenDemoAllowed;
  }
  
  // If not yet initialized, trigger initialization
  detectEnvironment();
  
  // Now return the frozen value (will be set by detectEnvironment)
  return frozenDemoAllowed ?? false; // Default to false for safety
}

/**
 * Check if demo mode is enabled via environment variable
 * 
 * This checks the VITE_DEMO_MODE environment variable.
 * When true, the application should NOT make any real API calls.
 * 
 * @returns true if demo mode is explicitly enabled
 */
export function isDemoModeEnabled(): boolean {
  return import.meta.env.VITE_DEMO_MODE === 'true' || import.meta.env.VITE_DEMO_MODE === true;
}

/**
 * Check if the application should connect to real APIs
 * 
 * Returns false if:
 * - Demo mode is enabled (VITE_DEMO_MODE=true)
 * - No API URL is configured (VITE_MDC_API_URL is empty/undefined)
 * 
 * @returns true if real API connections should be made
 */
export function shouldConnectToApi(): boolean {
  // If demo mode is explicitly enabled, don't connect
  if (isDemoModeEnabled()) {
    return false;
  }
  
  // If no API URL is configured, don't connect
  const apiUrl = import.meta.env.VITE_MDC_API_URL;
  if (!apiUrl || apiUrl.trim() === '') {
    return false;
  }
  
  return true;
}

/**
 * Get API URL for current environment
 * 
 * @returns API base URL
 */
export function getApiUrl(): string {
  // Explicit env var takes priority
  if (import.meta.env.VITE_MDC_API_URL) {
    return import.meta.env.VITE_MDC_API_URL;
  }
  
  const env = detectEnvironment();
  
  switch (env) {
    case 'production':
      return 'https://api.cyberraptor.ai';
    case 'staging':
      return 'https://staging-api.cyberraptor.ai';
    case 'test':
      return '';
    case 'development':
    default:
      return '';
  }
}

/**
 * Get complete environment configuration
 * 
 * @returns Full environment configuration object
 */
export function getEnvironmentConfig(): EnvironmentConfig {
  const env = detectEnvironment();
  
  return {
    environment: env,
    isProduction: env === 'production',
    isDevelopment: env === 'development',
    isTest: env === 'test',
    isDemoAllowed: isDemoAllowed(),
    apiUrl: getApiUrl(),
  };
}

// ============================================================================
// Singleton Export for Convenience
// ============================================================================

/**
 * Current environment (cached for performance)
 * Use this for simple checks, use getEnvironmentConfig() for full config
 */
export const ENV = {
  get current() { return detectEnvironment(); },
  get isProduction() { return isProduction(); },
  get isDevelopment() { return isDevelopment(); },
  get isStaging() { return isStaging(); },
  get isTest() { return isTest(); },
  get isDemoAllowed() { return isDemoAllowed(); },
  get apiUrl() { return getApiUrl(); },
} as const;

export default ENV;

// ============================================================================
// User Role Overrides (Production-Safe)
// ============================================================================

/**
 * Get user role overrides from environment variable
 * 
 * Format: VITE_USER_ROLE_OVERRIDES="email1:role1,email2:role2"
 * Example: VITE_USER_ROLE_OVERRIDES="ken@cyberraptor.ai:operator,admin@cyberraptor.ai:admin"
 * 
 * This is a PRODUCTION-SAFE way to configure user roles without backend changes.
 * Roles are configured at BUILD TIME via environment variables.
 * 
 * SECURITY NOTE: This is for UX routing only. Backend must still enforce RBAC.
 * 
 * @returns Map of email to role
 */
export function getUserRoleOverrides(): Map<string, string> {
  const overrides = new Map<string, string>();
  const rawOverrides = import.meta.env.VITE_USER_ROLE_OVERRIDES;
  
  if (!rawOverrides || typeof rawOverrides !== 'string') {
    return overrides;
  }
  
  // Parse "email1:role1,email2:role2" format
  const pairs = rawOverrides.split(',');
  for (const pair of pairs) {
    const [email, role] = pair.split(':').map(s => s.trim().toLowerCase());
    if (email && role) {
      overrides.set(email, role);
    }
  }
  
  if (overrides.size > 0 && import.meta.env.DEV) {
    console.log('[Environment] User role overrides configured:', Object.fromEntries(overrides));
  }
  
  return overrides;
}

/**
 * Get role for a specific email from environment overrides
 * 
 * @param email - User's email address
 * @returns Role string or undefined if not configured
 */
export function getRoleOverrideForEmail(email: string): string | undefined {
  const overrides = getUserRoleOverrides();
  return overrides.get(email.toLowerCase());
}
